import { AddressI, AddressType } from "../../pojo/AddressI";
import { DropDownItem } from "../../pojo/DropDownItem";
import type { CustomAttributes } from "../../pojo/CustomAttributes";
import { OrganizationService, WSContactRole, WSOrganization } from "../../services/organization.service";
import { S25Util } from "../../util/s25-util";
import { INGESTION_POINT_TYPE_ID } from "./data.ingestion.const";
import {
    CategoriesData,
    ContactRoleData,
    DataIngestionService,
    IngestionFieldDefinition,
} from "./data.ingestion.service";
import {
    findPrioritizedWorkflowSystemActionForItem,
    hydrateIngestionFieldFromCustomAttributeRows,
    parseFieldDefinitionIdFromWorkflowToken,
    stringFromMaybeNil,
} from "./ingestion.hydrate.util";
import type { IngestionRuntimeHydrateFn } from "./ingestion.runtime.hydrate.context";
import { Workflow } from "../workflow/service/Workflow";
import { WorkflowService } from "../workflow/service/workflow.service";
import { Category } from "../../pojo/Category";
import { Item } from "../../pojo/Item";

function indexAddressesByType(wsOrganization: WSOrganization): Map<number, AddressI> {
    const byType = new Map<number, AddressI>();
    const rows = S25Util.array.forceArray(wsOrganization.address) as AddressI[];
    for (const row of rows) {
        const addressType = Number(row.address_type || 0);
        if (!addressType || !Number.isFinite(addressType)) {
            continue;
        }
        byType.set(addressType, row);
    }
    return byType;
}

function readStreet(addressRow: AddressI | undefined): string {
    if (!addressRow) {
        return "";
    }
    return String(addressRow.street_address ?? "").trim();
}

function readAddressScalar(addressRow: AddressI | undefined, ...keys: string[]): string {
    if (!addressRow) {
        return "";
    }
    for (const key of keys) {
        const value = addressRow[key];
        if (value != null && String(value).trim() !== "") {
            return String(value).trim();
        }
    }
    return "";
}

function primaryContactIdFromWs(contacts: WSContactRole[]): number | undefined {
    for (const row of contacts) {
        if (S25Util.toBool(row.primary_contact) && row.contact_id) {
            return row.contact_id;
        }
    }
    return undefined;
}

function buildOrgContactRolePayload(contacts: WSContactRole[]): ContactRoleData {
    const items: ContactRoleData["items"] = [];
    for (const row of contacts) {
        if (!row.contact_id) {
            continue;
        }
        const roleId =
            row.contact_role_id != null && String(row.contact_role_id).trim() !== "" ? Number(row.contact_role_id) : -2;
        items.push({
            roleId: Number.isFinite(roleId) ? roleId : -2,
            roleName: row.contact_role,
            contactId: row.contact_id,
            contactName: row.contact_name,
        });
    }
    return { items };
}

async function enrichOrgContactRoleNames(fieldDef: IngestionFieldDefinition, payload: ContactRoleData): Promise<void> {
    if (!payload.items.length) {
        fieldDef.jsonFieldItem = { items: [] };
        return;
    }
    const roleIds = [...new Set(payload.items.map((item) => item.roleId))];
    const contactIds = [...new Set(payload.items.map((item) => item.contactId))];
    const [roleNames, contactNames] = await Promise.all([
        DataIngestionService.getObjectNames("ROLE", roleIds),
        DataIngestionService.getObjectNames("CONTACT", contactIds),
    ]);
    fieldDef.jsonFieldItem = {
        items: payload.items.map((item) => ({
            ...item,
            roleName: roleNames[`${item.roleId}-ROLE`] || item.roleName,
            contactName: contactNames[`${item.contactId}-CONTACT`] || item.contactName,
        })),
    };
}

function wsCategoryItems(wsOrganization: WSOrganization): DropDownItem[] {
    const rawList = S25Util.array.forceArray((wsOrganization as WSOrganization).category) as Category.DataI[];
    return rawList.map((row) => {
        const itemId = Number(row.category_id ?? 0);
        const itemName = String(row.category_name || (itemId ? `Category ${itemId}` : ""));
        return { itemId, itemName, itemTypeId: undefined, txt: itemName, val: String(itemId) } as DropDownItem;
    });
}

function setScalarOnField(fieldDef: IngestionFieldDefinition, value: string | number | undefined): boolean {
    if (value === undefined) {
        return false;
    }
    const stringValue = typeof value === "number" ? String(value) : value;
    fieldDef.longAnswer = stringValue;
    return true;
}

function extractValueForOrgWorkflowInput(
    inputCode: string,
    wsOrganization: WSOrganization,
    addressesByType: Map<number, AddressI>,
    contacts: WSContactRole[],
): string | number | undefined {
    switch (inputCode) {
        case "organizationIdField":
            return Number(wsOrganization.organization_id);
        case "acctNameField":
            return wsOrganization.organization_name;
        case "acctTitleField":
            return stringFromMaybeNil(wsOrganization.organization_title);
        case "acctNumberField":
            return String(wsOrganization.organization_number ?? "");
        case "orgTypeIdField":
            return wsOrganization.organization_type?.type_id;
        case "primeContactField":
            return primaryContactIdFromWs(contacts);
        case "commentField":
            return wsOrganization.comments ?? "";
        case "adminAddress":
            return readStreet(addressesByType.get(AddressType.Admin));
        case "adminCity":
            return readAddressScalar(addressesByType.get(AddressType.Admin), "city");
        case "adminState":
            return readAddressScalar(addressesByType.get(AddressType.Admin), "state_prov");
        case "adminZip":
            return readAddressScalar(addressesByType.get(AddressType.Admin), "zip_post");
        case "adminCountry":
            return readAddressScalar(addressesByType.get(AddressType.Admin), "country");
        case "adminPhone":
            return readAddressScalar(addressesByType.get(AddressType.Admin), "phone");
        case "adminFax":
            return readAddressScalar(addressesByType.get(AddressType.Admin), "fax");
        case "billingAddress":
            return readStreet(addressesByType.get(AddressType.Billing));
        case "billingCity":
            return readAddressScalar(addressesByType.get(AddressType.Billing), "city");
        case "billingState":
            return readAddressScalar(addressesByType.get(AddressType.Billing), "state_prov");
        case "billingZip":
            return readAddressScalar(addressesByType.get(AddressType.Billing), "zip_post");
        case "billingCountry":
            return readAddressScalar(addressesByType.get(AddressType.Billing), "country");
        case "billingPhone":
            return readAddressScalar(addressesByType.get(AddressType.Billing), "phone");
        case "billingFax":
            return readAddressScalar(addressesByType.get(AddressType.Billing), "fax");
        case "actionConfig":
        case "contactRoleFieldRefs":
        case "orgCategoryIds":
        case "orgCustomAttributes":
            return undefined;
        default:
            return undefined;
    }
}

function applyWorkflowInputHydration(
    fieldDefinitions: IngestionFieldDefinition[],
    workflowAction: Workflow.WorkflowAction | undefined,
    wsOrganization: WSOrganization,
    addressesByType: Map<number, AddressI>,
    contacts: WSContactRole[],
): boolean {
    if (!workflowAction?.inputs?.length) {
        return false;
    }
    let changed = false;
    const byFieldId = new Map<number, IngestionFieldDefinition>();
    for (const fieldDef of fieldDefinitions) {
        if (fieldDef.fieldDefinitionId != null) {
            byFieldId.set(fieldDef.fieldDefinitionId, fieldDef);
        }
    }
    for (const workflowInput of workflowAction.inputs) {
        const fieldDefinitionId = parseFieldDefinitionIdFromWorkflowToken(workflowInput.inputValue);
        if (fieldDefinitionId == null) {
            continue;
        }
        const fieldDef = byFieldId.get(fieldDefinitionId);
        if (!fieldDef) {
            continue;
        }
        const extracted = extractValueForOrgWorkflowInput(
            workflowInput.inputCode,
            wsOrganization,
            addressesByType,
            contacts,
        );
        if (extracted === undefined) {
            continue;
        }
        if (setScalarOnField(fieldDef, extracted)) {
            changed = true;
        }
    }
    return changed;
}

async function hydrateParticipatingTypedFields(
    fieldDefinitions: IngestionFieldDefinition[],
    wsOrganization: WSOrganization,
): Promise<boolean> {
    let changed = false;
    const contacts = S25Util.array.forceArray(wsOrganization.contact) as WSContactRole[];
    const orgAttributeRows = S25Util.array.forceArray(wsOrganization.custom_attribute) as CustomAttributes.DataI[];

    for (const fieldDef of fieldDefinitions) {
        if (fieldDef.fieldType === "ORG_CATEGORIES") {
            fieldDef.jsonFieldItem = { items: wsCategoryItems(wsOrganization) } as CategoriesData;
            changed = true;
        } else if (fieldDef.fieldType === "ORG_CONTACT_ROLE") {
            const payload = buildOrgContactRolePayload(contacts);
            await enrichOrgContactRoleNames(fieldDef, payload);
            changed = true;
        } else if (hydrateIngestionFieldFromCustomAttributeRows(fieldDef, orgAttributeRows, ["ORG_CUSTOM_ATTRIBUTE"])) {
            changed = true;
        } else if (fieldDef.fieldType === "ORG_RATING") {
            if (wsOrganization.rating_id != null && String(wsOrganization.rating_id).trim() !== "") {
                fieldDef.longAnswer = String(wsOrganization.rating_id);
                changed = true;
            }
        } else if (fieldDef.fieldType === "ORG_TYPE" && wsOrganization.organization_type?.type_id != null) {
            const typeId = wsOrganization.organization_type.type_id;
            fieldDef.longAnswer = String(typeId);
            fieldDef.specialFieldItem = {
                itemId: typeId,
                itemName: wsOrganization.organization_type.type_name ?? "",
                itemTypeId: undefined,
                txt: wsOrganization.organization_type.type_name ?? "",
                val: String(typeId),
            };
            changed = true;
        } else if (fieldDef.fieldType === "ORGANIZATION") {
            const orgId = Number(wsOrganization.organization_id);
            if (orgId && !Number.isNaN(orgId)) {
                fieldDef.longAnswer = String(orgId);
                fieldDef.specialFieldItem = {
                    itemId: orgId,
                    itemName: wsOrganization.organization_name ?? "",
                    itemTypeId: Item.Ids.Organization,
                    txt: wsOrganization.organization_name ?? "",
                    val: String(orgId),
                };
                changed = true;
            }
        }
    }
    return changed;
}

/**
 * Hydrates ingestion answers from the current organization row for forms that include org-scoped fields.
 * Also applies scalar mappings declared on the workflow `EDIT_ORG` (or `CREATE_ORG`) action for this ingestion point.
 */
export function createOrgEditHydrate(organizationId: number): IngestionRuntimeHydrateFn {
    return async (context): Promise<boolean> => {
        const wsOrganization = await OrganizationService.getOrganizationById(organizationId, [
            "contacts",
            "categories",
            "attributes",
            "address",
            "text",
        ]);
        if (!wsOrganization) {
            return false;
        }

        const submissionSnapshot = context.submission();
        const workflowProcessId =
            submissionSnapshot?.workflowProcessId ??
            context.ingestionPoint?.workflowId ??
            context.ingestionPoint?.defaultWorkflowId;

        let workflowProcess: Workflow.WorkflowProcess | undefined;
        if (workflowProcessId && !Number.isNaN(workflowProcessId)) {
            try {
                workflowProcess = await WorkflowService.getWorkflowProcess(workflowProcessId);
            } catch {
                workflowProcess = undefined;
            }
        }

        const addressesByType = indexAddressesByType(wsOrganization);
        const contacts = S25Util.array.forceArray(wsOrganization.contact) as WSContactRole[];

        const ingestionPointId = context.ingestionPoint?.ingestionPointId;
        const workflowAction =
            ingestionPointId != null
                ? findPrioritizedWorkflowSystemActionForItem(
                      workflowProcess,
                      INGESTION_POINT_TYPE_ID,
                      Number(ingestionPointId),
                      ["EDIT_ORG", "CREATE_ORG"],
                  )
                : undefined;

        const mappedChanged = applyWorkflowInputHydration(
            context.fieldDefinitions,
            workflowAction,
            wsOrganization,
            addressesByType,
            contacts,
        );
        const typedChanged = await hydrateParticipatingTypedFields(context.fieldDefinitions, wsOrganization);

        return typedChanged || mappedChanged;
    };
}
